import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Star, Search, Filter, TrendingUp, TrendingDown, AlertTriangle, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import LanguageSwitcher from '../components/LanguageSwitcher';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CompaniesPage = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('rating');

  const fetchCompanies = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/companies`);
      setCompanies(data);
    } catch (error) {
      toast.error(t('toast.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const filterAndSortCompanies = useCallback(() => {
    let filtered = [...companies];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(company =>
        company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        company.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(company => {
        const rating = company.average_rating;
        switch (selectedCategory) {
          case 'excellent':
            return rating >= 4.5;
          case 'good':
            return rating >= 4.0 && rating < 4.5;
          case 'average':
            return rating >= 3.0 && rating < 4.0;
          case 'poor':
            return rating >= 2.0 && rating < 3.0;
          case 'scam':
            return rating < 2.0;
          default:
            return true;
        }
      });
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'rating':
          return b.average_rating - a.average_rating;
        case 'reviews':
          return b.review_count - a.review_count;
        case 'name':
          return a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });

    setFilteredCompanies(filtered);
  }, [companies, searchTerm, selectedCategory, sortBy]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  useEffect(() => {
    filterAndSortCompanies();
  }, [filterAndSortCompanies]);

  const getCategoryBadge = (rating) => {
    if (rating >= 4.5) return { text: t('companies.badgeExcellent'), color: 'bg-green-100 text-green-800 border-green-300' };
    if (rating >= 4.0) return { text: t('companies.badgeRecommended'), color: 'bg-blue-100 text-blue-800 border-blue-300' };
    if (rating >= 3.0) return { text: t('companies.badgeAverage'), color: 'bg-yellow-100 text-yellow-800 border-yellow-300' };
    if (rating >= 2.0) return { text: t('companies.badgePoor'), color: 'bg-orange-100 text-orange-800 border-orange-300' };
    return { text: t('companies.badgeAvoid'), color: 'bg-red-100 text-red-800 border-red-300' };
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${star <= Math.round(rating) ? 'fill-yellow-500 text-yellow-500' : 'text-slate-300'}`}
          />
        ))}
      </div>
    );
  };

  const handleLogout = async () => {
    await logout();
    toast.success(t('toast.loggedOut'));
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title="Tutti i Broker Forex - Confronta Recensioni e Opinioni"
        description="Confronta oltre 35 broker forex con recensioni verificate. Filtra per categoria: Eccellenti, Consigliati, Medi, Da Evitare. Trova il broker giusto per te."
        keywords="confronto broker forex, migliori broker forex, lista broker affidabili, broker forex Italia, AWStocks, Interactive Invests, broker regolamentati"
      />
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center">
            <h1 className="text-2xl font-black text-blue-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              ForexForum
            </h1>
          </Link>
          <div className="flex gap-3 items-center">
            <LanguageSwitcher />
            {user ? (
              <>
                <span className="text-sm text-slate-600 hidden sm:inline" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  {user.name}
                </span>
                {user.role === 'admin' && (
                  <Button
                    onClick={() => navigate('/admin')}
                    data-testid="admin-dashboard-btn"
                    className="bg-blue-900 text-white hover:bg-blue-800"
                    size="sm"
                  >
                    {t('common.admin')}
                  </Button>
                )}
                <Button
                  onClick={handleLogout}
                  data-testid="logout-btn"
                  variant="outline"
                  className="border-slate-300 text-slate-700 hover:bg-slate-50"
                  size="sm"
                >
                  {t('common.logout')}
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={() => navigate('/login')}
                  variant="outline"
                  className="border-slate-300 text-slate-700 hover:bg-slate-50"
                  size="sm"
                >
                  {t('common.login')}
                </Button>
                <Button
                  onClick={() => navigate('/register')}
                  className="bg-blue-900 text-white hover:bg-blue-800"
                  size="sm"
                >
                  {t('common.signUp')}
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-6 pt-6">
        <Link to="/" className="inline-flex items-center text-blue-900 hover:text-blue-800" data-testid="back-to-home">
          <ArrowLeft className="w-5 h-5 mr-2" />
          <span className="font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>{t('common.backToHome')}</span>
        </Link>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <h2 className="text-4xl font-black text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
          {t('companies.pageTitle')}
        </h2>
        <p className="text-slate-600 mb-8" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          {t('companies.pageSubtitle', { count: companies.length })}
        </p>

        {/* Filters */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 mb-8 shadow-sm">
          <div className="grid md:grid-cols-3 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                <Search className="w-4 h-4 inline mr-2" />
                {t('companies.searchLabel')}
              </label>
              <Input
                data-testid="search-input"
                type="text"
                placeholder={t('companies.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full"
              />
            </div>

            {/* Category Filter */}
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                <Filter className="w-4 h-4 inline mr-2" />
                {t('companies.categoryLabel')}
              </label>
              <select
                data-testid="category-filter"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full bg-white border border-slate-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-900"
              >
                <option value="all">{t('companies.all', { count: companies.length })}</option>
                <option value="excellent">⭐ {t('companies.excellent')}</option>
                <option value="good">✓ {t('companies.good')}</option>
                <option value="average">➖ {t('companies.average')}</option>
                <option value="poor">⚠️ {t('companies.poor')}</option>
                <option value="scam">🚫 {t('companies.scam')}</option>
              </select>
            </div>

            {/* Sort */}
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                <TrendingUp className="w-4 h-4 inline mr-2" />
                {t('companies.sortLabel')}
              </label>
              <select
                data-testid="sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full bg-white border border-slate-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-900"
              >
                <option value="rating">{t('companies.sortRating')}</option>
                <option value="reviews">{t('companies.sortReviews')}</option>
                <option value="name">{t('companies.sortName')}</option>
              </select>
            </div>
          </div>

          {/* Results Count */}
          <div className="mt-4 pt-4 border-t border-slate-200">
            <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              {t('companies.showing')} <span className="font-bold text-slate-900">{filteredCompanies.length}</span> {t('companies.of')} {companies.length} {t('companies.brokers')}
            </p>
          </div>
        </div>

        {/* Companies List */}
        {loading ? (
          <div className="text-center text-slate-600 py-12">{t('common.loading')}</div>
        ) : filteredCompanies.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
            <Search className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 text-lg" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              {t('companies.noResults')}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredCompanies.map((company) => {
              const badge = getCategoryBadge(company.average_rating);
              return (
                <Link
                  key={company.id}
                  to={`/company/${company.id}`}
                  data-testid={`company-card-${company.id}`}
                  className="block bg-white border border-slate-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-all duration-200 hover:border-blue-300"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-2xl font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                          {company.name}
                        </h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold border ${badge.color}`}>
                          {badge.text}
                        </span>
                      </div>
                      
                      <p className="text-slate-700 mb-3 line-clamp-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        {company.description}
                      </p>

                      <div className="flex items-center gap-6">
                        <div className="flex items-center gap-2">
                          {renderStars(company.average_rating)}
                          <span className="text-2xl font-bold text-slate-900">
                            {company.average_rating.toFixed(1)}
                          </span>
                        </div>
                        <div className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                          {company.review_count} {t('companies.reviews')}
                        </div>
                      </div>
                    </div>

                    {company.logo_url && (
                      <img
                        src={company.logo_url}
                        alt={company.name}
                        className="w-20 h-20 rounded-lg object-cover"
                      />
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CompaniesPage;
